import { apiClient } from '@lib/axios';
import type { User, LoginCredentials, Centre, MainCentre } from '@appTypes/auth';
import type { Session } from '@appTypes/shell';

export const authService = {
  login: (credentials: LoginCredentials) =>
    apiClient.post<{ accessToken: string; user: User }>('/auth/login', credentials),

  logout: () => apiClient.post('/auth/logout'),

  refresh: () =>
    apiClient.post<{ accessToken: string; user: User }>('/auth/refresh'),

  me: () => apiClient.get<{ user: User }>('/auth/me'),

  forgotPassword: (email: string) =>
    apiClient.post('/auth/forgot-password', { email }),

  // Both lists are public reads — the login screen needs them before sign-in.
  getMainCentres: () =>
    apiClient.get<{ items: MainCentre[]; total: number }>('/main-centres', { params: { limit: 100 } }),

  // Pass a main centre to list only its centres (the login screen's second
  // dropdown); omit it for every centre the caller can see.
  getCentres: (mainCentreId?: string) =>
    apiClient.get<{ items: Centre[]; total: number }>('/centres', {
      params: { limit: 100, ...(mainCentreId ? { mainCentreId } : {}) },
    }),

  getCurrentSession: () =>
    apiClient.get<{ session: { _id: string } }>('/sessions/current'),

  getSessions: () =>
    apiClient.get<{ items: Session[] }>('/sessions'),

  /**
   * Every selectable session label (2000-01 → current), newest first, plus the
   * one to pre-select. Unlike getSessions this is NOT limited to sessions that
   * already exist as records — the server derives the range from today's date,
   * so the new year appears on its own each April.
   */
  getSessionOptions: () =>
    apiClient.get<{ items: string[]; current: string }>('/sessions/options'),

  resendVerification: () =>
    apiClient.post('/auth/resend-verification'),

  verifyEmail: (token: string) =>
    apiClient.post('/auth/verify-email', { token }),

  patchMe: (name: string) =>
    apiClient.patch<{ user: User }>('/auth/me', { name }),

  uploadAvatar: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<{ user: User }>('/auth/avatar', fd);
  },

  resetPassword: (token: string, password: string) =>
    apiClient.post('/auth/reset-password', { token, password }),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiClient.post('/auth/change-password', { currentPassword, newPassword }),
};
